import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { SealCheck, Camera, X, Plus, Eye, EyeSlash, Pause, Play, Trash, ShieldCheck } from "@phosphor-icons/react";
import { mediaUrl, TagInput } from "@/pages/match/MatchOnboarding";

export default function MatchProfile({ profile, onChanged }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    display_name: profile.display_name || "", bio: profile.bio || "",
    city: profile.city || "", region: profile.region || "", country: profile.country || "",
    languages: profile.languages || [], interests: profile.interests || [], photos: profile.photos || [],
  });
  const [prefs, setPrefs] = useState(profile.prefs || { age_min: 25, age_max: 45, verified_only: false });
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const refresh = () => { qc.invalidateQueries({ queryKey: ["match-me"] }); qc.invalidateQueries({ queryKey: ["match-discovery"] }); onChanged?.(); };

  const save = useMutation({ mutationFn: async () => (await api.put("/match/profile", f)).data,
    onSuccess: () => { toast.success("Profile saved"); refresh(); }, onError: (e) => toast.error(apiError(e)) });
  const savePrefs = useMutation({ mutationFn: async () => (await api.put("/match/preferences", prefs)).data,
    onSuccess: () => { toast.success("Preferences saved"); refresh(); }, onError: (e) => toast.error(apiError(e)) });
  const verify = useMutation({ mutationFn: async () => (await api.post("/match/verify/request", {})).data,
    onSuccess: (r) => { toast.success(r.note || "Verification requested"); refresh(); }, onError: (e) => toast.error(apiError(e)) });
  const state = useMutation({ mutationFn: async ({ path, on, msg }) => { const r = (await api.post(path, { on })).data; return { r, msg }; },
    onSuccess: ({ msg }) => { toast.success(msg); refresh(); }, onError: (e) => toast.error(apiError(e)) });
  const del = useMutation({ mutationFn: async () => (await api.delete("/match/profile")).data,
    onSuccess: () => { toast.success("Your Match profile was deleted. Your iYeneh account is unchanged."); refresh(); }, onError: (e) => toast.error(apiError(e)) });

  const upload = async (file) => {
    if (!file) return; setUploading(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/match/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photos", [...f.photos, { url: data.url }]);
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  return (
    <div className="space-y-6" data-testid="match-profile">
      {/* status controls */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card p-3">
        <span className="mr-auto text-sm font-medium">
          {profile.verified ? <span className="inline-flex items-center gap-1 text-sky-500"><SealCheck size={16} weight="fill" /> Verified</span>
            : profile.verification_status === "pending" ? <span className="text-amber-500">Verification pending</span>
            : <button data-testid="profile-verify" onClick={() => verify.mutate()} className="inline-flex items-center gap-1 text-sky-500 hover:underline"><ShieldCheck size={16} weight="fill" /> Get verified</button>}
        </span>
        <Toggle testid="profile-pause" active={profile.paused} onLabel="Resume" offLabel="Pause"
          onIcon={Play} offIcon={Pause} onClick={() => state.mutate({ path: "/match/pause", on: !profile.paused, msg: profile.paused ? "Resumed" : "Paused" })} />
        <Toggle testid="profile-hide" active={profile.hidden} onLabel="Unhide" offLabel="Hide"
          onIcon={Eye} offIcon={EyeSlash} onClick={() => state.mutate({ path: "/match/hide", on: !profile.hidden, msg: profile.hidden ? "Visible again" : "Hidden" })} />
      </div>

      {/* photos */}
      <Section title={`Photos (${f.photos.length}/5)`}>
        <div className="flex flex-wrap gap-2">
          {f.photos.map((p, i) => (
            <div key={i} className="relative h-24 w-24 overflow-hidden rounded-xl border border-border">
              <img src={mediaUrl(p.url)} alt="" className="h-full w-full object-cover" />
              <button onClick={() => set("photos", f.photos.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X size={11} /></button>
            </div>
          ))}
          {f.photos.length < 5 && (
            <label data-testid="profile-photo-upload" className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border text-xs text-muted-foreground hover:border-rose-500">
              <Camera size={20} />{uploading ? "…" : "Add"}<input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            </label>
          )}
        </div>
      </Section>

      {/* details */}
      <Section title="About you">
        <div className="space-y-4">
          <input data-testid="profile-name" value={f.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder="Display name" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
          <textarea data-testid="profile-bio" value={f.bio} onChange={(e) => set("bio", e.target.value)} rows={3} maxLength={600} placeholder="About me" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
          <div className="grid gap-3 md:grid-cols-3">
            <input data-testid="profile-city" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className="rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            <input data-testid="profile-region" value={f.region} onChange={(e) => set("region", e.target.value)} placeholder="Region/State" className="rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            <input data-testid="profile-country" value={f.country} onChange={(e) => set("country", e.target.value)} placeholder="Country" className="rounded-xl border border-border bg-background px-3 py-2 text-sm" />
          </div>
          <div><p className="mb-1.5 text-sm font-medium">Languages</p><TagInput testid="profile-languages" value={f.languages} onChange={(v) => set("languages", v)} placeholder="Add a language + Enter" /></div>
          <div><p className="mb-1.5 text-sm font-medium">Interests</p><TagInput testid="profile-interests" value={f.interests} onChange={(v) => set("interests", v)} placeholder="Add an interest + Enter" /></div>
          <button data-testid="profile-save" onClick={() => save.mutate()} disabled={save.isPending} className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">{save.isPending ? "Saving…" : "Save profile"}</button>
        </div>
      </Section>

      {/* preferences */}
      <Section title="Discovery preferences">
        <div className="space-y-4">
          <div>
            <p className="mb-1.5 text-sm font-medium">Age range: {prefs.age_min}–{prefs.age_max}</p>
            <div className="flex items-center gap-2">
              <input data-testid="pref-age-min" type="number" min={18} max={99} value={prefs.age_min} onChange={(e) => setPrefs({ ...prefs, age_min: +e.target.value })} className="w-20 rounded-xl border border-border bg-background px-2 py-2 text-sm" />
              <span className="text-muted-foreground">to</span>
              <input data-testid="pref-age-max" type="number" min={18} max={99} value={prefs.age_max} onChange={(e) => setPrefs({ ...prefs, age_max: +e.target.value })} className="w-20 rounded-xl border border-border bg-background px-2 py-2 text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm" data-testid="pref-verified-only">
            <input type="checkbox" checked={prefs.verified_only} onChange={(e) => setPrefs({ ...prefs, verified_only: e.target.checked })} className="h-4 w-4 accent-rose-500" /> Verified profiles only
          </label>
          <button data-testid="pref-save" onClick={() => savePrefs.mutate()} disabled={savePrefs.isPending} className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-rose-500 disabled:opacity-60">Save preferences</button>
        </div>
      </Section>

      {/* danger */}
      <Section title="Danger zone">
        <button data-testid="profile-delete" onClick={() => { if (window.confirm("Delete your Match profile? Your iYeneh account stays active.")) del.mutate(); }}
          className="inline-flex items-center gap-2 rounded-full border border-destructive/40 px-5 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/10">
          <Trash size={16} weight="fill" /> Delete Match profile
        </button>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return <div className="rounded-3xl border border-border bg-card p-5"><h3 className="mb-4 font-display text-base font-semibold">{title}</h3>{children}</div>;
}
function Toggle({ active, onLabel, offLabel, onIcon: On, offIcon: Off, onClick, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium ${active ? "border-amber-400 bg-amber-50 text-amber-600 dark:bg-amber-950/30" : "border-border"}`}>
    {active ? <On size={15} weight="fill" /> : <Off size={15} weight="fill" />}{active ? onLabel : offLabel}</button>;
}
